import { useRef, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import { OverflowTip } from './Common/Hover'
import type { AiChatSession } from '../services/aiChatHistoryService'
import { StatusChip } from './Common/Chip'
import { FloatingMenu } from './Common/FloatingMenu'
import { Button } from './Common/Button'
import { IconButton } from './Common/IconButton'
import { Pressable } from './Common/Pressable'
import { useEdgeFadeScroll } from '../hooks/useEdgeFadeScroll'
import { useNls } from '../nls/nlsProvider'
import { useRailPageShellCollapse } from './Common/railPageShellContext'
import { getAiChatHistoryCopy } from './AiMagicNls'

const displayTitle = (session: AiChatSession, fallback: string) => {
  const trimmed = session.title.trim()
  return trimmed.length > 0 ? trimmed : fallback
}

const sessionInitial = (label: string): string => {
  const trimmed = label.trim()
  if (!trimmed) return '·'
  return trimmed.charAt(0).toUpperCase()
}

const HistorySessionRow = ({ session, active, open, copy, shellCollapsed, onOpenMenu, onCloseMenu, onOpen, onRename, onDelete }: {
  session: AiChatSession
  active: boolean
  open: boolean
  copy: ReturnType<typeof getAiChatHistoryCopy>
  shellCollapsed: boolean
  onOpenMenu: () => void
  onCloseMenu: () => void
  onOpen: () => void
  onRename: () => void
  onDelete: () => void
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const label = displayTitle(session, copy.newChat)
  const chatAttachment = session.attachments?.find((attachment) => attachment.kind === 'chat')
  const sourceLabel = chatAttachment
    ? `${chatAttachment.messages?.length ?? 0} chats · #${chatAttachment.channelName ?? chatAttachment.channelId ?? 'chat'}`
    : null
  return (
    <div className={`aiChatHistoryRow${active ? ' isActive' : ''}`}>
      <Pressable
        className="aiChatHistoryOpen"
        onClick={onOpen}
        aria-current={active ? 'true' : undefined}
        aria-label={shellCollapsed ? label : undefined}
        title={shellCollapsed ? label : undefined}
      >
        {shellCollapsed && (
          <span className="aiChatHistoryInitial" aria-hidden="true">{sessionInitial(label)}</span>
        )}
        <OverflowTip label={label} placement="right" group="aiChatHistory" offset={14}>
          <span className="aiChatHistoryOpenLabel">{label}</span>
        </OverflowTip>
        {sourceLabel ? (
          <StatusChip
            size="sm"
            tone="neutral"
            className="aiChatHistorySourcePill"
            icon={<ChatBubbleOutlineRoundedIcon fontSize="inherit" />}
          >
            {sourceLabel}
          </StatusChip>
        ) : null}
      </Pressable>
      <IconButton
        ref={triggerRef}
        icon={<MoreHorizRoundedIcon fontSize="inherit" />}
        label={copy.actionsFor(label)}
        variant="tertiary"
        size="sm"
        className="aiChatHistoryMore"
        aria-expanded={open}
        onClick={open ? onCloseMenu : onOpenMenu}
      />
      <FloatingMenu anchorRef={triggerRef} isOpen={open} onClose={onCloseMenu} placement="bottom-end" className="aiChatHistoryMenu" role="menu" ariaLabel={copy.actionsFor(label)}>
        <Pressable role="menuitem" onClick={() => { onCloseMenu(); onRename() }}>{copy.rename}</Pressable>
        <Pressable role="menuitem" className="isDanger" onClick={() => { onCloseMenu(); onDelete() }}>{copy.delete}</Pressable>
      </FloatingMenu>
    </div>
  )
}

export const AiChatHistoryPanel = ({ sessions, activeId, onOpen, onNew, onRename, onDelete }: {
  sessions: AiChatSession[]
  activeId: string | null
  onOpen: (session: AiChatSession) => void
  onNew: () => void
  onRename: (session: AiChatSession) => void
  onDelete: (session: AiChatSession) => void
}) => {
  const { t } = useNls()
  const copy = getAiChatHistoryCopy(t)
  const [menuId, setMenuId] = useState<string | null>(null)
  const visibleSessions = sessions
  const { ref: listRef, scrollProps } = useEdgeFadeScroll<HTMLDivElement>()
  const { collapsed: shellCollapsed } = useRailPageShellCollapse()
  const effectiveCollapsed = shellCollapsed
  return (
    <aside
      className={`aiChatHistory${effectiveCollapsed ? ' isCollapsed' : ''}${shellCollapsed ? ' isShellCollapsed' : ''}`}
      aria-label={copy.ariaHistory}
    >
      <Button
        variant="tertiary"
        size="lg"
        className="aiChatNewButton"
        onClick={onNew}
        title={effectiveCollapsed ? copy.newChat : undefined}
        aria-label={copy.newChat}
        icon={<AddRoundedIcon fontSize="inherit" />}
      >
        {!effectiveCollapsed && copy.newChat}
      </Button>
      {(!effectiveCollapsed || shellCollapsed) && <div
        ref={listRef}
        className="aiChatHistoryList edgeFadeScroll"
        {...scrollProps}
      >
        {visibleSessions.map((session) => <HistorySessionRow
          key={session.id}
          session={session}
          active={activeId === session.id}
          open={menuId === session.id}
          copy={copy}
          shellCollapsed={shellCollapsed}
          onOpenMenu={() => setMenuId(session.id)}
          onCloseMenu={() => setMenuId(null)}
          onOpen={() => onOpen(session)}
          onRename={() => onRename(session)}
          onDelete={() => onDelete(session)}
        />)}
      </div>}
    </aside>
  )
}
